import { Component } from '@theme/component';
import { getCart, invalidateCart } from '@theme/cart-upsell-state';
import { fetchConfig, preloadImage, ensureImageWidth, onAnimationEnd } from '@theme/utilities';
// CartUpdateEvent and VariantUpdateEvent are used for JSDoc type annotations
// eslint-disable-next-line no-unused-vars
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Decodes HTML entities from escaped data attributes into readable text.
 * @param {string} value
 * @returns {string}
 */
const decodeHtmlEntities = (value) => {
  if (!value || !value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button and adds loading state.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.classList.add('is-loading');
  }

  /**
   * Enables the add to cart button and removes loading state.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
    this.refs.addToCartButton.classList.remove('is-loading');
  }

  /**
   * Sets disabled state without applying loading treatment.
   * @param {boolean} disabled
   */
  setDisabledState(disabled) {
    this.refs.addToCartButton.disabled = disabled;
    this.refs.addToCartButton.classList.remove('is-loading');
  }

  /**
   * Removes the loading state from the button.
   */
  removeLoadingState() {
    this.refs.addToCartButton.classList.remove('is-loading');
  }

  /**
   * Handles the click event for the add to cart button.
   */
  handleClick() {
    if (this.dataset.notifyMeMode === 'true') return;

    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest('product-form-component')
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (image) preloadImage(image);

    const upsellImage = ensureImageWidth(this.dataset.upsellImage?.trim(), 450);
    if (upsellImage) preloadImage(upsellImage);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // If the product is internationally restricted, disable the button immediately
    // without waiting for a variant selection event.
    if (this.dataset.internationalRestricted === 'true') {
      this.refs.addToCartButtonContainer?.setDisabledState(true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString(),
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector('quantity-selector-component')
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      const cart = await getCart();
      return this.#updateCartQuantityFromData(/** @type {Cart} */ (cart));
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === 'product-form-component'
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Returns current variant SKU for this mini-PDP form (if available).
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  #getCurrentMiniPdpSku(form) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="id"]')
    );
    const variantId = variantIdInput?.value;
    if (!variantId) return '';
    const miniPdpRoot = this.closest('.mini-pdp');
    const variantsScript = miniPdpRoot?.querySelector(
      'mini-pdp-variant-selector script[type="application/json"]',
    );
    if (!(variantsScript instanceof HTMLScriptElement) || !variantsScript.textContent) return '';
    try {
      const variants = JSON.parse(variantsScript.textContent);
      if (!Array.isArray(variants)) return '';
      const match = variants.find((variant) => String(variant?.id) === String(variantId));
      return match?.sku ?? '';
    } catch {
      return '';
    }
  }

  /**
   * Fallback append for custom line-item property configured on mini-PDP forms.
   * Guarantees the property is present in FormData even if the hidden input was not serialized.
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   */
  #appendConfiguredLineItemProperty(form, formData) {
    const key = this.dataset.lineItemPropertyKey?.trim();
    if (!key) return;
    const propertyName = `properties[${key}]`;
    if (formData.has(propertyName)) return;

    const skuTemplate = this.dataset.lineItemPropertySkuTemplate?.trim() || '';
    const skuSuffix = this.dataset.lineItemPropertySkuSuffix?.trim() || '';
    const baseValue = this.dataset.lineItemPropertyValue ?? '';
    let value = baseValue;

    if (skuTemplate || skuSuffix) {
      const sku = this.#getCurrentMiniPdpSku(form);
      value = skuTemplate ? skuTemplate.split('{{sku}}').join(sku) : `${sku}${skuSuffix}`;
      if (!value && baseValue) value = baseValue;
    }

    formData.append(propertyName, value);
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if a size option exists but none is selected
    const section = this.closest(
      '.mini-pdp__product-content, .shopify-section, dialog, product-card',
    );
    const sizeFieldset = section?.querySelector('[data-fieldset-option-position="2"]');
    if (sizeFieldset) {
      const hasCheckedSize = sizeFieldset.querySelector('input[type="radio"]:checked');
      if (!hasCheckedSize) {
        const sizeError = /** @type {HTMLElement | null} */ (
          section?.querySelector('.size-select-error')
        );
        if (sizeError) {
          sizeError.classList.remove('hidden');
          this.#setLiveRegionText(sizeError.textContent?.trim() || '');
        }
        return;
      }
    }

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled,
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace(
          '{{ maximum }}',
          validation.maxQuantity?.toString() || '',
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    this.#appendConfiguredLineItemProperty(form, formData);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // Disable ATC buttons during the request's duration
    for (const container of allAddToCartContainers) {
      container.disable();
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Build productInfo for ATC success popup (message, product image, upsell fetch).
          // Use the container that matches the variant just added so the popup shows the correct size/variant.
          const addedVariantId = id.toString();
          const matchingContainer = Array.from(allAddToCartContainers).find(
            (c) => c.getAttribute?.('data-variant-id') === addedVariantId,
          );
          const addToCartContainer = matchingContainer ?? allAddToCartContainers[0];
          const variantTitle =
            addToCartContainer?.getAttribute?.('data-product-variant-title') ?? '';
          const selectedSizeLabel = this.#getSelectedSizeLabel();
          const variantSize =
            selectedSizeLabel ||
            addToCartContainer?.getAttribute?.('data-product-variant-size') ||
            variantTitle;
          const variantImage =
            addToCartContainer?.getAttribute?.('data-product-variant-media') ??
            this.dataset.productImage ??
            '';
          const productHandle = this.dataset.productHandle ?? '';
          const productInfo = productHandle
            ? {
                productHandle,
                productTitle: this.dataset.productTitle ?? '',
                variantTitle: variantTitle || (this.dataset.productTitle ?? ''),
                sizeLabel: variantSize || variantTitle,
                imageUrl: variantImage || this.dataset.productImage || null,
              }
            : undefined;

          const upsellProductId = this.dataset.upsellProductId?.trim();
          const upsellInfo =
            upsellProductId && this.dataset.upsellProductHandle
              ? {
                  pairedProductId: upsellProductId,
                  pairedProductHandle: this.dataset.upsellProductHandle?.trim() ?? '',
                  title: this.dataset.upsellProductTitle?.trim() ?? '',
                  imageUrl: this.dataset.upsellImage?.trim() || null,
                  upsellTitle: this.dataset.upsellTitle?.trim() ?? '',
                  upsellText: this.dataset.upsellText?.trim() ?? '',
                  upsellButtonText: this.dataset.upsellButtonText?.trim() ?? '',
                }
              : undefined;

          // Dispatch cart update immediately when we have section HTML so the drawer morphs
          // and listeners (e.g. mini PDP close) run without waiting on an extra cart fetch.
          const fromShopTheLook = this.closest('[data-from-shop-the-look]');
          /** @type {{ source: string, itemCount: number, productId?: string, sections?: Record<string, string>, productInfo?: { productHandle: string, productTitle?: string, variantTitle?: string, sizeLabel?: string, imageUrl?: string|null }, upsellInfo?: { pairedProductId: string, pairedProductHandle: string, title: string, imageUrl: string | null, upsellTitle?: string, upsellText: string, upsellButtonText: string } }} */
          const cartAddData = {
            source: fromShopTheLook ? 'mini-pdp-shop-the-look' : 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
            productInfo,
            upsellInfo,
          };
          // Clear cached cart data before using it post-addition
          invalidateCart();
          this.dispatchEvent(new CartAddEvent({}, id.toString(), cartAddData));

          // Update quantity label in the background; brief delay so getCart() can reuse shared cache from popup/upsell.
          setTimeout(() => this.#fetchAndUpdateCartQuantity(), 80);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // Re-enable ATC buttons once complete
        for (const container of allAddToCartContainers) {
          container.enable();
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */ (newElement.cloneNode(true)),
      );
    }
  }

  /**
   * Syncs a hidden input by name from newly rendered product-form HTML.
   * @param {Document | Element} html
   * @param {string} inputName
   */
  #syncHiddenInputByName(html, inputName) {
    const currentForm = this.querySelector('form');
    const newForm = html.querySelector('product-form-component form');
    if (!currentForm || !newForm) return;

    const currentInput = /** @type {HTMLInputElement | null} */ (
      currentForm.querySelector(`input[type="hidden"][name="${inputName}"]`)
    );
    const newInput = /** @type {HTMLInputElement | null} */ (
      newForm.querySelector(`input[type="hidden"][name="${inputName}"]`)
    );

    if (currentInput && newInput) {
      currentInput.value = newInput.value;
      return;
    }

    if (currentInput && !newInput) {
      currentInput.remove();
      return;
    }

    if (!currentInput && newInput) {
      currentForm.appendChild(newInput.cloneNode(true));
    }
  }

  /**
   * Sync preorder line item properties from variant-updated HTML.
   * @param {Document | Element} html
   */
  #syncPreorderLineItemProperties(html) {
    this.#syncHiddenInputByName(html, 'properties[_preOrderDate]');
    this.#syncHiddenInputByName(html, 'properties[Preorder]');
  }

  /**
   * Sync preorder line item properties from local variant data.
   * @param {{ preorder_raw_date?: string, preorder_property_message?: string, preorder_message?: string } | null} variant
   */
  #syncLocalPreorderLineItemProperties(variant) {
    const form = this.querySelector('form');
    if (!form) return;

    const preorderDateInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="properties[_preOrderDate]"]')
    );
    const preorderMessageInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="properties[Preorder]"]')
    );

    const preorderRawDate = variant?.preorder_raw_date ?? '';
    const preorderMessage = variant?.preorder_property_message ?? variant?.preorder_message ?? '';
    const hasPreorderData = Boolean(preorderRawDate && preorderMessage);

    if (!hasPreorderData) {
      preorderDateInput?.remove();
      preorderMessageInput?.remove();
      return;
    }

    const dateInput = preorderDateInput || document.createElement('input');
    dateInput.type = 'hidden';
    dateInput.name = 'properties[_preOrderDate]';
    dateInput.value = preorderRawDate;

    const messageInput = preorderMessageInput || document.createElement('input');
    messageInput.type = 'hidden';
    messageInput.name = 'properties[Preorder]';
    messageInput.value = preorderMessage;

    if (!preorderDateInput) {
      form.appendChild(dateInput);
    }
    if (!preorderMessageInput) {
      form.appendChild(messageInput);
    }
  }

  /**
   * Update add-to-cart text from local variant data.
   * @param {{ available?: boolean, preorder_message?: string, inventory_management?: unknown } | null} variant
   * @param {AddToCartComponent} addToCartButtonContainer
   */
  #updateLocalAddToCartText(variant, addToCartButtonContainer) {
    const addToCartButton = addToCartButtonContainer.refs?.addToCartButton;
    if (!addToCartButton) return;

    // Keep restricted CTA copy stable during local variant updates.
    // The restricted message is product-level and already rendered by Liquid.
    if (this.dataset.internationalRestricted === 'true') return;

    const textInner = addToCartButton.querySelector('.add-to-cart-text__content-inner');
    if (!textInner) return;

    const defaultAddToCartText = decodeHtmlEntities(
      addToCartButtonContainer.dataset.addToCartDefaultText?.trim() || '',
    );
    const unavailableText = decodeHtmlEntities(
      addToCartButtonContainer.dataset.unavailableText?.trim() || '',
    );
    const notifyMeText = decodeHtmlEntities(
      addToCartButtonContainer.dataset.notifyMeText?.trim() || '',
    );
    const notifyMeIconSvg = addToCartButtonContainer.dataset.notifyMeIconSvg || '';
    const internationalRestrictedText = this.dataset.internationalRestrictedText?.trim() || '';
    const isInternationalRestricted = this.dataset.internationalRestricted === 'true';
    const shouldUseNotifyMeText =
      !isInternationalRestricted &&
      variant?.available === false &&
      String(variant?.inventory_management) === 'shopify';
    const localText = isInternationalRestricted
      ? internationalRestrictedText || unavailableText
      : shouldUseNotifyMeText
        ? notifyMeText || unavailableText
        : variant?.available === true
          ? variant?.preorder_message?.trim() || defaultAddToCartText
          : unavailableText;

    if (!localText) return;

    /** @type {Node[]} */
    const nextNodes = [];

    if (shouldUseNotifyMeText && notifyMeIconSvg) {
      const iconWrapper = document.createElement('span');
      iconWrapper.className = 'svg-wrapper add-to-cart-icon';
      iconWrapper.innerHTML = notifyMeIconSvg;
      nextNodes.push(iconWrapper);
    }

    nextNodes.push(document.createTextNode(localText));
    textInner.replaceChildren(...nextNodes);
  }

  /**
   * Returns the currently selected size label from the local variant picker.
   * @returns {string}
   */
  #getSelectedSizeLabel() {
    const scope = this.closest('.shopify-section, dialog, product-card') || this;
    /** @type {string[]} */
    const selectors = [
      // Main PDP size options are rendered with this class.
      'fieldset.variant-option--default input[type="radio"]:checked',
      // Legacy/fallback markup path used in some templates.
      'fieldset[data-fieldset-option-position="2"] input[type="radio"]:checked',
      // Mini PDP uses a dedicated fieldset class.
      '.mini-pdp__variants-fieldset input[type="radio"]:checked',
    ];

    for (const selector of selectors) {
      const checkedSizeInput = /** @type {HTMLInputElement | null} */ (
        scope.querySelector(selector)
      );
      if (checkedSizeInput?.value) return checkedSizeInput.value;
    }

    return '';
  }

  /**
   * Updates notify-me related button/trigger attributes from local variant data.
   * @param {{ id?: string | number, available?: boolean, inventory_management?: unknown, title?: string, sku?: string, price?: string | number, compare_at_price?: string | number | null } | null} variant
   * @param {AddToCartComponent} addToCartButtonContainer
   */
  #syncLocalNotifyMeAttributes(variant, addToCartButtonContainer) {
    const addToCartButton = addToCartButtonContainer.refs?.addToCartButton;
    if (!addToCartButton || !variant?.id) return;

    const isInternationalRestricted = this.dataset.internationalRestricted === 'true';
    if (isInternationalRestricted) {
      delete this.dataset.notifyMeMode;
      delete addToCartButtonContainer.dataset.notifyMeMode;
      addToCartButton.type = 'submit';
      addToCartButton.removeAttribute('data-open-notify-me');
      return;
    }

    const shouldUseNotifyMeMode =
      !isInternationalRestricted &&
      variant.available === false &&
      String(variant.inventory_management) === 'shopify';
    const sizeLabel = this.#getSelectedSizeLabel();

    addToCartButtonContainer.setAttribute('data-variant-id', String(variant.id));
    addToCartButton.setAttribute('data-variant-id', String(variant.id));
    if (sizeLabel) {
      addToCartButton.setAttribute('data-size-label', sizeLabel);
      addToCartButtonContainer.setAttribute('data-product-variant-size', sizeLabel);
    }

    if (variant.title != null) {
      addToCartButton.setAttribute('data-variant-title', variant.title);
      addToCartButtonContainer.setAttribute('data-product-variant-title', variant.title);
    }
    if (variant.sku != null) {
      addToCartButton.setAttribute('data-variant-sku', variant.sku);
    }
    if (variant.price != null) {
      addToCartButton.setAttribute('data-variant-price', String(variant.price));
    }
    if (variant.compare_at_price != null) {
      addToCartButton.setAttribute(
        'data-variant-compare-at-price',
        String(variant.compare_at_price),
      );
    } else {
      addToCartButton.removeAttribute('data-variant-compare-at-price');
    }

    if (shouldUseNotifyMeMode) {
      this.dataset.notifyMeMode = 'true';
      addToCartButtonContainer.dataset.notifyMeMode = 'true';
      addToCartButton.type = 'button';
      addToCartButton.setAttribute('data-open-notify-me', '');
    } else {
      delete this.dataset.notifyMeMode;
      delete addToCartButtonContainer.dataset.notifyMeMode;
      addToCartButton.type = 'submit';
      addToCartButton.removeAttribute('data-open-notify-me');
    }
  }

  /**
   * Checks if the form is currently in notify me mode.
   * @returns {boolean}
   */
  #isInNotifyMeMode() {
    return this.dataset.notifyMeMode === 'true';
  }

  /**
   * Checks if the new HTML indicates notify me mode.
   * @param {Document | Element} html - The HTML to check
   * @returns {boolean}
   */
  #isNewHtmlInNotifyMeMode(html) {
    if (!html || typeof html.querySelector !== 'function') return false;
    const newProductForm = /** @type {HTMLElement | null} */ (
      html.querySelector('product-form-component')
    );
    return newProductForm?.dataset.notifyMeMode === 'true';
  }

  /**
   * Updates the notify me drawer variant ID when switching between unavailable variants.
   * Note: The description text is rendered by Liquid and updated via morph, not JS.
   * @param {string | number} variantId - The new variant ID
   */
  #updateNotifyMeDrawer(variantId) {
    const notifyMeVariantId = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="notifyMeVariantId"]')
    );

    if (notifyMeVariantId) {
      notifyMeVariantId.value = String(variantId);
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    const responseHtml = event.detail.data.html;
    const isLocalOnlyUpdate = responseHtml === document;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Check if the new variant is in notify me mode
    const isCurrentlyNotifyMe = this.#isInNotifyMeMode();
    const isInternationalRestricted = this.dataset.internationalRestricted === 'true';
    const isNewNotifyMe = isLocalOnlyUpdate
      ? Boolean(
          event.detail.resource &&
          !isInternationalRestricted &&
          event.detail.resource.available === false &&
          String(event.detail.resource.inventory_management) === 'shopify',
        )
      : this.#isNewHtmlInNotifyMeMode(responseHtml);

    // Update the data attribute for notify me mode
    if (isNewNotifyMe) {
      this.dataset.notifyMeMode = 'true';
    } else {
      delete this.dataset.notifyMeMode;
    }

    const { acceleratedCheckoutButtonContainer } = this.refs;
    // In notify-me mode, the add-to-cart-component is inside a dialog-component and not in refs
    // So we need to query for it directly
    const currentAddToCartButtonContainer =
      this.refs.addToCartButtonContainer ||
      /** @type {AddToCartComponent | null} */ (
        this.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]')
      );
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs?.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Remove loading state now that variant data has loaded
    currentAddToCartButtonContainer.removeLoadingState();

    if (isLocalOnlyUpdate) {
      const isInternationalRestricted = this.dataset.internationalRestricted === 'true';
      this.#updateLocalAddToCartText(
        /** @type {{ available?: boolean, preorder_message?: string } | null} */ (
          event.detail.resource
        ),
        currentAddToCartButtonContainer,
      );
      this.#syncLocalNotifyMeAttributes(
        /** @type {{ id?: string | number, available?: boolean, inventory_management?: unknown, title?: string, sku?: string, price?: string | number, compare_at_price?: string | number | null } | null} */ (
          event.detail.resource
        ),
        currentAddToCartButtonContainer,
      );
      this.#syncLocalPreorderLineItemProperties(
        /** @type {{ preorder_raw_date?: string, preorder_property_message?: string, preorder_message?: string } | null} */ (
          event.detail.resource
        ),
      );

      if (isInternationalRestricted) {
        currentAddToCartButtonContainer.setDisabledState(true);
      } else if (!isNewNotifyMe) {
        if (
          event.detail.resource == null ||
          event.detail.resource.available == false ||
          this.dataset.internationalRestricted === 'true'
        ) {
          currentAddToCartButtonContainer.setDisabledState(true);
        } else {
          currentAddToCartButtonContainer.setDisabledState(false);
        }
      } else {
        // Notify-me mode must remain clickable even though the variant is unavailable.
        currentAddToCartButtonContainer.setDisabledState(false);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (
          event.detail.resource == null ||
          event.detail.resource.available == false ||
          this.dataset.internationalRestricted === 'true'
        ) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      return;
    }

    // If switching to/from notify me mode, we need to morph the entire button container
    if (isCurrentlyNotifyMe !== isNewNotifyMe) {
      // Get the parent span containing the add-to-cart button
      // Note: We need to find the span wrapper, not the add-to-cart-component itself
      const currentButtonWrapper =
        currentAddToCartButtonContainer.closest('span[data-notify-me-mode]') ||
        currentAddToCartButtonContainer.closest('dialog-component')?.parentElement ||
        currentAddToCartButtonContainer.parentElement;
      const newButtonWrapper =
        responseHtml.querySelector('span[data-notify-me-mode]') ||
        responseHtml.querySelector('add-to-cart-component')?.parentElement;

      if (currentButtonWrapper && newButtonWrapper) {
        morph(currentButtonWrapper, newButtonWrapper);
      }
    } else {
      // Normal update - just update button state and text
      // Don't disable button if in notify me mode
      if (this.dataset.internationalRestricted === 'true') {
        currentAddToCartButtonContainer.setDisabledState(true);
      } else if (!isNewNotifyMe) {
        if (
          event.detail.resource == null ||
          event.detail.resource.available == false ||
          this.dataset.internationalRestricted === 'true'
        ) {
          currentAddToCartButtonContainer.setDisabledState(true);
        } else {
          currentAddToCartButtonContainer.setDisabledState(false);
        }
      }

      const newAddToCartButton = event.detail.data.html.querySelector(
        'product-form-component [ref="addToCartButton"]',
      );
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
        // Ensure loading state is removed after morph
        currentAddToCartButtonContainer.removeLoadingState();
      }
    }

    // Update notify me drawer variant info only when staying in notify me mode (not when switching into it)
    // When switching into notify-me mode, the morph already updated the drawer with correct content
    // Note: We only update the variant ID, not the description text (which is rendered by Liquid)
    if (isNewNotifyMe && isCurrentlyNotifyMe && event.detail.resource) {
      this.#updateNotifyMeDrawer(event.detail.resource.id);
    }

    this.#syncPreorderLineItemProperties(responseHtml);

    if (acceleratedCheckoutButtonContainer) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available == false ||
        this.dataset.internationalRestricted === 'true'
      ) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attributes for the product variant media, title, and id so ATC success popup shows the correct variant.
    // For PDP we show only the size in the success message: use the server-rendered Size option from the new HTML.
    if (event.detail.resource) {
      const resource = event.detail.resource;
      const resourceWithTitle =
        /** @type {{ id: string, title?: string, featured_media?: { preview_image?: { src?: string } } }} */ (
          resource
        );
      currentAddToCartButtonContainer?.setAttribute('data-variant-id', String(resource.id));
      const productVariantMedia = resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        currentAddToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=326',
        );
      }
      const productVariantTitle = resourceWithTitle.title ?? '';
      currentAddToCartButtonContainer?.setAttribute(
        'data-product-variant-title',
        productVariantTitle,
      );
      const newAddToCartContainer = responseHtml.querySelector('add-to-cart-component');
      const sizeFromServer = newAddToCartContainer?.getAttribute?.('data-product-variant-size');
      currentAddToCartButtonContainer?.setAttribute(
        'data-product-variant-size',
        sizeFromServer ?? productVariantTitle,
      );
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      responseHtml.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
      );
    }

    const newQuantityRules = responseHtml.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = responseHtml.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = responseHtml.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          responseHtml.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, responseHtml.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = responseHtml.querySelector('volume-pricing');
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons,
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   * Note: Don't disable in notify me mode since button opens drawer instead of submitting.
   * @param {CustomEvent} event
   */
  #onVariantSelected = (event) => {
    const selection = /** @type {{ loadsNewProduct?: boolean, hasLocalVariant?: boolean }} */ (
      event.detail?.resource || {}
    );
    const shouldUseBlockingLoading =
      selection.loadsNewProduct === true || selection.hasLocalVariant !== true;

    if (this.#isInNotifyMeMode()) return;

    if (shouldUseBlockingLoading) {
      this.refs.addToCartButtonContainer?.disable();
    } else {
      this.refs.addToCartButtonContainer?.removeLoadingState();
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
